/**
 * Hero (+ page) lazy video activator.
 * - MP4 data-src only (no HLS)
 * - Hero: delay first play until after window load + idle (poster first = cheap open)
 * - Hidden desktop/mobile wrappers never load
 * - In-app / reduced-motion: poster only
 */
function isWrapperHidden(el) {
  const wrap = el.closest('.hero__media-wrapper');
  if (!wrap) return false;
  return window.getComputedStyle(wrap).display === 'none';
}

function activateLazyVideo(video) {
  if (!(video instanceof HTMLVideoElement)) return;
  if (video.dataset.edLazyReady === '1') {
    if (video.dataset.edAutoplayVisible !== 'false') {
      video.play().catch(() => {});
    }
    return;
  }

  let loaded = false;
  video.querySelectorAll('source[data-src]').forEach((source) => {
    if (!source.getAttribute('src')) {
      source.setAttribute('src', source.dataset.src);
      loaded = true;
    }
  });

  if (loaded) {
    try {
      video.load();
    } catch {
      /* ignore */
    }
  }

  video.dataset.edLazyReady = '1';
  video.muted = true;
  if (video.dataset.edAutoplayVisible !== 'false') {
    video.play().catch(() => {});
  }
}

function releaseLazyVideo(video) {
  if (!(video instanceof HTMLVideoElement)) return;
  video.pause();
  let changed = false;
  video.querySelectorAll('source[src]').forEach((source) => {
    if (!source.dataset.src) {
      source.dataset.src = source.getAttribute('src');
    }
    source.removeAttribute('src');
    changed = true;
  });
  if (changed) {
    try {
      video.load();
    } catch {
      /* ignore */
    }
    video.dataset.edLazyReady = '0';
  }
}

function whenBrowserQuiet(cb) {
  const run = () => {
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(cb, { timeout: 2500 });
    } else {
      setTimeout(cb, 1200);
    }
  };
  if (document.readyState === 'complete') {
    run();
  } else {
    window.addEventListener('load', run, { once: true });
  }
}

function initHeroVideoPerformance() {
  const perf = (typeof window !== 'undefined' && window.__edPerf) || {};
  const videos = document.querySelectorAll(
    '.hero video.hero__media, video[data-ed-lazy-video], video.slide__video'
  );
  if (!videos.length) return;

  if (perf.noVideoAutoplay) {
    videos.forEach((v) => {
      try {
        v.pause();
        v.autoplay = false;
        v.removeAttribute('autoplay');
        v.preload = 'none';
        releaseLazyVideo(v);
      } catch {
        /* ignore */
      }
    });
    return;
  }

  document.querySelectorAll('.hero .hero__media-wrapper').forEach((wrap) => {
    if (window.getComputedStyle(wrap).display === 'none') {
      wrap.querySelectorAll('video').forEach(releaseLazyVideo);
    }
  });

  const heroVideos = [];
  const otherVideos = [];
  videos.forEach((v) => {
    if (v.closest('.hero')) heroVideos.push(v);
    else otherVideos.push(v);
  });

  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const v = entry.target;
        if (!(v instanceof HTMLVideoElement)) return;
        if (isWrapperHidden(v)) {
          releaseLazyVideo(v);
          return;
        }

        if (entry.isIntersecting && entry.intersectionRatio > 0.05) {
          // Hero: wait for idle so first paint isn't competing with video
          if (v.closest('.hero')) {
            if (v.dataset.edHeroQueued === '1') return;
            v.dataset.edHeroQueued = '1';
            whenBrowserQuiet(() => {
              if (!isWrapperHidden(v) && v.getBoundingClientRect().bottom > 0) {
                activateLazyVideo(v);
              }
            });
          } else {
            activateLazyVideo(v);
          }
        } else {
          v.pause();
          if (!entry.isIntersecting) {
            releaseLazyVideo(v);
            delete v.dataset.edHeroQueued;
          }
        }
      });
    },
    { threshold: [0, 0.05, 0.15], rootMargin: '40px 0px' }
  );

  [...heroVideos, ...otherVideos].forEach((v) => {
    if (isWrapperHidden(v)) {
      releaseLazyVideo(v);
      return;
    }
    io.observe(v);
  });

  let resizeT;
  window.addEventListener(
    'resize',
    () => {
      clearTimeout(resizeT);
      resizeT = setTimeout(() => {
        document.querySelectorAll('.hero .hero__media-wrapper').forEach((wrap) => {
          const hidden = window.getComputedStyle(wrap).display === 'none';
          wrap.querySelectorAll('video').forEach((v) => {
            if (hidden) releaseLazyVideo(v);
          });
        });
      }, 150);
    },
    { passive: true }
  );
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initHeroVideoPerformance);
} else {
  initHeroVideoPerformance();
}

document.addEventListener('shopify:section:load', initHeroVideoPerformance);
